import { ReparoApi } from './index';
import type { LoginPayloadResponse } from './consultantInvite';

/** One person with a login on the brand, as Settings › Users lists them. */
export type TeamMember = {
    userId: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    role: string;
    joinedAt: string;
};

/** One invitation nobody has accepted yet. */
export type PendingTeamInvite = {
    inviteId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    /** The inviter's display name, when their login is still on the brand. */
    invitedBy: string | null;
    sentAt: string;
    expiresAt: string;
    /** Past its expiry: the link in their inbox is dead until someone resends. */
    expired: boolean;
};

export type TeamView = {
    members: TeamMember[];
    pendingInvites: PendingTeamInvite[];
};

export type TeamInvitePublicView = {
    email: string;
    firstName: string | null;
    lastName: string | null;
    brandName: string | null;
    expiresAt: string;
};

export type TeamInviteIssuedView = {
    inviteId: string;
    email: string;
    link: string;
    /** False when the mail did not go - the link is then the admin's to deliver by hand. */
    emailed: boolean;
    /** True when a pending invite for this address was refreshed and re-sent, not duplicated. */
    reused: boolean;
};

export const getTeam = (): Promise<TeamView> =>
    ReparoApi<TeamView>({ method: 'GET', url: '/team' });

export const createTeamInvite = (payload: {
    email: string;
    firstName?: string;
    lastName?: string;
}): Promise<TeamInviteIssuedView> =>
    ReparoApi<TeamInviteIssuedView>({
        method: 'POST',
        url: '/team-invites',
        body: JSON.stringify(payload),
    });

export const revokeTeamInvite = (inviteId: string): Promise<void> =>
    ReparoApi<void>({
        method: 'DELETE',
        url: `/team-invites/${encodeURIComponent(inviteId)}`,
    });

export const getTeamInvite = (token: string): Promise<TeamInvitePublicView> =>
    ReparoApi<TeamInvitePublicView>({
        method: 'GET',
        url: `/team-invites/${encodeURIComponent(token)}`,
    });

export const acceptTeamInvite = (payload: {
    token: string;
    password: string;
    firstName?: string;
    lastName?: string;
}): Promise<LoginPayloadResponse> =>
    ReparoApi<LoginPayloadResponse>({
        method: 'POST',
        url: '/team-invites/accept',
        body: JSON.stringify(payload),
    });
